<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import type { Pattern } from '@cross-my-heart/shared'
import { useLocalStore, type LocalPattern, type Collection } from '../composables/useLocalStore'
import PatternPreview from '../components/PatternPreview.vue'
import TourOverlay from '../components/TourOverlay.vue'
import PageLayout from '@/components/layout/PageLayout.vue'
import { CHARGE } from '../utils/voice'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface PatternDisplay {
  id: string
  title: string
  author?: string
  description?: string
  width: number
  height: number
  stitchCount: number
  colorCount: number
  createdAt: string
  tags: string[]
  parsedPattern: Pattern | null
}

const store = useLocalStore()
const patterns = ref<LocalPattern[]>([])
const collections = ref<Collection[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
const searchQuery = ref('')
const showTour = ref(false)

const TOUR_STEPS = [
  {
    selector: '[data-testid="pattern-grid"]',
    title: 'Your pattern library',
    body: 'All your patterns live here. Tap any card to open the workspace and start stitching.',
  },
  {
    selector: '[data-testid="new-pattern-trigger"]',
    title: 'Add a pattern',
    body: 'Tap “+ New Pattern” to import an .oxs file, browse curated designs, or start from a blank grid.',
  },
  {
    selector: '[data-testid="pattern-card"]',
    title: 'Open the workspace',
    body: 'Tap any pattern card to track your progress and mark stitches as you go.',
  },
]

async function completeTour() {
  await store.setSetting('tourSeen', true)
  showTour.value = false
}

/** null means "All patterns" (unfiltered). */
const activeCollectionId = ref<string | null>(null)

// ─── New collection form ───────────────────────────────────────────────────

const showNewCollection = ref(false)
const newCollectionName = ref('')
const creatingCollection = ref(false)

async function submitNewCollection() {
  const name = newCollectionName.value.trim()
  if (!name) return
  creatingCollection.value = true
  try {
    const c = await store.createCollection(name)
    collections.value = [...collections.value, c]
    newCollectionName.value = ''
    showNewCollection.value = false
    activeCollectionId.value = c.id
  } finally {
    creatingCollection.value = false
  }
}

function onNewCollectionKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter') submitNewCollection()
  if (e.key === 'Escape') cancelNewCollection()
}

function cancelNewCollection() {
  showNewCollection.value = false
  newCollectionName.value = ''
}

// ─── Rename ────────────────────────────────────────────────────────────────

const renamingId = ref<string | null>(null)
const renameValue = ref('')

function startRename(c: Collection) {
  renamingId.value = c.id
  renameValue.value = c.name
}

async function commitRename(id: string) {
  const name = renameValue.value.trim()
  if (name) {
    await store.renameCollection(id, name)
    collections.value = collections.value.map((c) => (c.id === id ? { ...c, name } : c))
  }
  renamingId.value = null
}

function onRenameKeydown(e: KeyboardEvent, id: string) {
  if (e.key === 'Enter') commitRename(id)
  if (e.key === 'Escape') renamingId.value = null
}

// ─── Delete collection ─────────────────────────────────────────────────────

async function deleteCollection(id: string) {
  await store.deleteCollection(id)
  collections.value = collections.value.filter((c) => c.id !== id)
  if (activeCollectionId.value === id) activeCollectionId.value = null
}

// ─── Pattern display ───────────────────────────────────────────────────────

const patternsDisplay = computed<PatternDisplay[]>(() => {
  return patterns.value.map((p) => ({
    id: p.id,
    title: p.title,
    author: p.pattern.info.author,
    description: p.pattern.info.description ?? undefined,
    width: p.pattern.dimensions.width,
    height: p.pattern.dimensions.height,
    stitchCount: p.pattern.stitches.length,
    colorCount: p.pattern.palette.length,
    createdAt: p.createdAt,
    tags: p.tags ?? [],
    parsedPattern: p.pattern,
  }))
})

const activeCollection = computed<Collection | null>(
  () => collections.value.find((c) => c.id === activeCollectionId.value) ?? null
)

const collectionFiltered = computed<PatternDisplay[]>(() => {
  if (!activeCollectionId.value) return patternsDisplay.value
  const ids = new Set(activeCollection.value?.patternIds ?? [])
  return patternsDisplay.value.filter((p) => ids.has(p.id))
})

const filteredPatterns = computed<PatternDisplay[]>(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return collectionFiltered.value
  return collectionFiltered.value.filter(
    (p) => p.title.toLowerCase().includes(q) || (p.description ?? '').toLowerCase().includes(q)
  )
})

onMounted(async () => {
  try {
    await store.initDB()
    const [allPatterns, allCollections, tourSeen] = await Promise.all([
      store.getAllPatterns(),
      store.getAllCollections(),
      store.getSetting('tourSeen'),
    ])
    patterns.value = allPatterns
    collections.value = allCollections
    if (!tourSeen) showTour.value = true
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load patterns'
    console.error('Failed to load patterns:', e)
  } finally {
    loading.value = false
  }
})

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString()
}
</script>

<template>
  <PageLayout>
    <template #title>
      <h1 class="text-2xl font-bold text-foreground">My Patterns</h1>
    </template>
    <template #actions>
      <!-- Single entry point — the second tap (the menu) branches to import / browse / blank.
           Collapsing the old Browse + Import buttons keeps the toolbar to one line on a phone (#105). -->
      <DropdownMenu>
        <DropdownMenuTrigger
          class="inline-flex items-center gap-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors text-sm"
          data-testid="new-pattern-trigger"
        >
          + New Pattern
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-48">
          <DropdownMenuItem as-child>
            <router-link
              to="/patterns/import"
              class="w-full cursor-pointer"
              data-testid="new-pattern-import"
            >
              Import .oxs file
            </router-link>
          </DropdownMenuItem>
          <DropdownMenuItem as-child>
            <router-link
              to="/patterns/browse"
              class="w-full cursor-pointer"
              data-testid="new-pattern-browse"
            >
              Browse featured
            </router-link>
          </DropdownMenuItem>
          <DropdownMenuItem as-child>
            <router-link
              to="/patterns/new"
              class="w-full cursor-pointer"
              data-testid="new-pattern-blank"
            >
              Start blank
            </router-link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </template>

    <!-- Error display -->
    <div
      v-if="error"
      class="mb-4 p-4 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive"
    >
      {{ error }}
    </div>

    <div v-if="loading" class="text-center py-12 text-muted-foreground">Loading patterns...</div>

    <template v-else>
      <!-- Collections filter bar -->
      <div class="mb-6 flex flex-wrap gap-2 items-center" data-testid="collections-bar">
        <!-- All patterns pill -->
        <button
          :class="[
            'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
            activeCollectionId === null
              ? 'bg-primary-500 text-white'
              : 'bg-muted text-foreground hover:bg-accent',
          ]"
          data-testid="collection-all"
          @click="activeCollectionId = null"
        >
          All patterns
          <span class="ml-1 opacity-70">({{ patterns.length }})</span>
        </button>

        <!-- Collection pills -->
        <div
          v-for="c in collections"
          :key="c.id"
          class="flex items-center gap-0.5"
          :data-testid="`collection-pill-${c.id}`"
        >
          <template v-if="renamingId === c.id">
            <input
              v-model="renameValue"
              class="px-2 py-1 text-sm border border-border rounded-l-full focus:outline-hidden focus:ring-1 focus:ring-primary-500 w-32"
              :data-testid="`collection-rename-input-${c.id}`"
              @blur="commitRename(c.id)"
              @keydown="onRenameKeydown($event, c.id)"
            />
            <button
              class="px-2 py-1.5 text-sm bg-muted text-foreground rounded-r-full border border-border border-l-0"
              @click="commitRename(c.id)"
            >
              ✓
            </button>
          </template>
          <template v-else>
            <button
              :class="[
                'px-3 py-1.5 rounded-l-full text-sm font-medium transition-colors',
                activeCollectionId === c.id
                  ? 'bg-primary-500 text-white'
                  : 'bg-muted text-foreground hover:bg-accent',
              ]"
              :data-testid="`collection-btn-${c.id}`"
              @click="activeCollectionId = c.id"
            >
              {{ c.name }}
              <span class="ml-1 opacity-70">({{ c.patternIds.length }})</span>
            </button>
            <div class="flex">
              <button
                :class="[
                  'px-1.5 py-1.5 text-xs transition-colors',
                  activeCollectionId === c.id
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-muted text-muted-foreground hover:bg-accent',
                ]"
                :aria-label="`Rename collection ${c.name}`"
                :data-testid="`collection-rename-btn-${c.id}`"
                @click.stop="startRename(c)"
              >
                ✎
              </button>
              <button
                :class="[
                  'px-1.5 py-1.5 text-xs rounded-r-full transition-colors',
                  activeCollectionId === c.id
                    ? 'bg-primary-600 text-white hover:bg-destructive'
                    : 'bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
                ]"
                :aria-label="`Delete collection ${c.name}`"
                :data-testid="`collection-delete-btn-${c.id}`"
                @click.stop="deleteCollection(c.id)"
              >
                ×
              </button>
            </div>
          </template>
        </div>

        <!-- New collection -->
        <template v-if="showNewCollection">
          <input
            v-model="newCollectionName"
            placeholder="Collection name…"
            class="px-3 py-1.5 text-sm border border-border rounded-full focus:outline-hidden focus:ring-2 focus:ring-primary-500 w-44"
            data-testid="new-collection-input"
            @keydown="onNewCollectionKeydown"
          />
          <button
            :disabled="creatingCollection || !newCollectionName.trim()"
            class="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-full hover:bg-primary/90 disabled:opacity-50 transition-colors"
            data-testid="new-collection-submit"
            @click="submitNewCollection"
          >
            Add
          </button>
          <button
            class="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            @click="cancelNewCollection"
          >
            Cancel
          </button>
        </template>
        <button
          v-else
          class="px-3 py-1.5 text-sm text-foreground hover:text-primary font-medium"
          data-testid="new-collection-btn"
          @click="showNewCollection = true"
        >
          + New collection
        </button>
      </div>

      <!-- Search bar — only when there are patterns to search -->
      <div v-if="patterns.length > 0" class="mb-6">
        <input
          v-model="searchQuery"
          type="search"
          placeholder="Search patterns…"
          class="w-full sm:max-w-xs px-4 py-2 border border-border rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <!-- Pattern grid — always rendered so the Create New card is always accessible.
           Status messages (empty collection, no search results) sit below the grid. -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pattern-grid">
        <!-- Create New Pattern card — always first in the grid -->
        <router-link
          to="/patterns/new"
          class="bg-card rounded-xl shadow-xs overflow-hidden hover:shadow-md transition-shadow border-2 border-dashed border-border hover:border-primary-400 flex flex-col items-center justify-center min-h-[220px] gap-3 text-muted-foreground hover:text-primary-500 transition-colors"
          data-testid="create-pattern-card"
        >
          <span class="text-4xl leading-none">+</span>
          <span class="text-sm font-medium">Create new pattern</span>
        </router-link>

        <router-link
          v-for="pattern in filteredPatterns"
          :key="pattern.id"
          :to="`/patterns/${pattern.id}`"
          class="bg-card rounded-xl shadow-xs overflow-hidden hover:shadow-md transition-shadow"
          data-testid="pattern-card"
        >
          <div class="aspect-square bg-muted flex items-center justify-center overflow-hidden">
            <PatternPreview :pattern="pattern.parsedPattern" :max-width="200" :max-height="200" />
          </div>
          <div class="p-4">
            <h3 class="font-semibold text-foreground truncate">{{ pattern.title }}</h3>
            <p v-if="pattern.author" class="text-sm text-muted-foreground truncate">
              by {{ pattern.author }}
            </p>
            <div class="mt-2 flex gap-3 text-xs text-muted-foreground">
              <span>{{ pattern.stitchCount.toLocaleString() }} stitches</span>
              <span>{{ pattern.colorCount }} colors</span>
            </div>
            <div class="mt-1 text-xs text-muted-foreground">
              Added {{ formatDate(pattern.createdAt) }}
            </div>
            <!-- Tag chips — show up to 3, indicate overflow -->
            <div v-if="pattern.tags.length > 0" class="mt-2 flex flex-wrap gap-1">
              <span
                v-for="tag in pattern.tags.slice(0, 3)"
                :key="tag"
                class="px-1.5 py-0.5 bg-muted text-foreground text-xs rounded-full border border-border"
                data-testid="pattern-tag-chip"
              >
                {{ tag }}
              </span>
              <span
                v-if="pattern.tags.length > 3"
                class="px-1.5 py-0.5 text-muted-foreground text-xs"
              >
                +{{ pattern.tags.length - 3 }} more
              </span>
            </div>
          </div>
        </router-link>
      </div>

      <!-- Status messages below the grid -->

      <!-- Empty library hint — shown only when there are no patterns at all (not a filtered view) -->
      <div
        v-if="patterns.length === 0 && !activeCollectionId"
        class="text-center py-8 text-sm text-muted-foreground"
      >
        {{ CHARGE }} Have an .oxs file?
        <router-link to="/patterns/import" class="text-foreground hover:text-primary">
          Import it
        </router-link>
        to begin.
      </div>

      <div
        v-if="activeCollectionId !== null && collectionFiltered.length === 0"
        class="text-center py-10"
        data-testid="empty-collection"
      >
        <div class="text-5xl mb-4">📂</div>
        <p class="text-muted-foreground mb-1">
          "{{ activeCollection?.name }}" has no patterns yet.
        </p>
        <p class="text-sm text-muted-foreground">
          Open a pattern and add it to this collection from the pattern detail page.
        </p>
      </div>

      <div
        v-else-if="patterns.length > 0 && filteredPatterns.length === 0 && searchQuery"
        class="text-center py-10"
      >
        <p class="text-muted-foreground">No patterns found for "{{ searchQuery }}".</p>
        <button
          class="mt-2 text-foreground hover:text-primary text-sm font-medium"
          @click="searchQuery = ''"
        >
          Clear search
        </button>
      </div>
    </template>
  </PageLayout>

  <TourOverlay v-model="showTour" :steps="TOUR_STEPS" @complete="completeTour" />
</template>
